import React, { useState, useMemo } from "react";
import {
  Typography,
  Grid,
  Box,
  Divider,
  Popover,
  Button,
  Badge,
} from "@material-ui/core";
import { deepOrange, grey, green } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import {
  getLocationFullName,
  getLocationViewName,
  isBulkLocationAvailable,
} from "../../../utils/locationUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import { useInboundContext } from "./context";

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    width: "100%",
  },
  bayContainer: {
    display: "flex",
    justifyContent: "space-around",
    border: "1px solid #b0bec5",
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(0.5),
    flexWrap: "wrap",
    // width: "100%",
  },
  locationAvatar: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(
      theme.palette.type === "dark" ? grey[400] : grey[300],
    ),
    backgroundColor: theme.palette.type === "dark" ? grey[400] : grey[300],
    borderColor: theme.palette.type === "dark" ? grey[400] : grey[300],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  locationUsedAvatar: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(
      theme.palette.type === "dark" ? grey[400] : grey[300],
    ),
    backgroundColor: theme.palette.type === "dark" ? grey[400] : grey[300],
    borderColor:
      theme.palette.type === "dark" ? deepOrange[500] : deepOrange[500],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  locationUsedAvailableAvatar: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(
      theme.palette.type === "dark" ? green[600] : green[600],
    ),
    backgroundColor: theme.palette.type === "dark" ? green[600] : green[600],
    borderColor:
      theme.palette.type === "dark" ? deepOrange[500] : deepOrange[500],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  selectedLocationAvatar: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
    borderColor: deepOrange[500],
    "&:hover": {
      cursor: "pointer",
    },
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  locationAvailableAvatar: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: green[600],
    "&:hover": {
      cursor: "pointer",
    },
    borderColor: green[600],
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "3px solid",
    borderRadius: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  redCircle: {
    position: "absolute",
    top: theme.spacing(-1.25),
    left: theme.spacing(-1.25),
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderStyle: "solid",
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    borderRadius: theme.spacing(1.5),
    backgroundColor: deepOrange[500],
    color: theme.palette.getContrastText(deepOrange[600]),
  },
  backdrop: {
    zIndex: 1001,
  },
  popvover: {
    padding: theme.spacing(1),
  },
  badge: {
    "&:hover": {
      cursor: "pointer",
    },
  },
}));

export const BulkInboundAisle = () => {
  const { state, dispatch } = useInboundContext();

  const { selectedLocation, disableSelection, bayList, showInventoryQuantity } =
    state;

  const classes = useStyles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelectLocation = (location, evt) => {
    if (disableSelection) return;
    evt.preventDefault();
    evt.stopPropagation();
    setAnchorEl(null);
    const payload = location.id === selectedLocation?.id ? null : location;
    dispatch({ type: "SET_SELECTED_LOCATION", payload });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getLocationCapcityUsedText = (location) => {
    if (!showInventoryQuantity) {
      const bulkCapacity = location.bulkCapacity || 1;
      const val = Number((location.capacityUsed || 0) / bulkCapacity) * 100;
      return `${val.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
    }
    const capacityUsed = Number(location.capacityUsed || 0).toLocaleString(
      "en-US",
      { maximumFractionDigits: 0 },
    );
    return `${capacityUsed || 0} ${getUnitName(location.bulkUnits)}`;
  };

  const getLocationBulkCapacityText = () => {
    const bulkCapacity = Number(
      selectedLocation.bulkCapacity || 0,
    ).toLocaleString("en-US", { maximumFractionDigits: 0 });
    return `${bulkCapacity} ${getUnitName(selectedLocation.bulkUnits)}`;
  };

  const getTotalPopoverContent = () => {
    return (
      <div
        className={classes.popvover}
        style={!isSmall ? { width: "24rem" } : null}
      >
        <Grid container>
          <Grid item xs={12}>
            <Box mb={1}>Location {getLocationFullName(selectedLocation)}</Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={1}>
              <Divider />
            </Box>
          </Grid>
          <Grid item xs={5}>
            Total capacity
          </Grid>
          <Grid item xs={7}>
            {getLocationBulkCapacityText()}
          </Grid>
          <Grid item xs={5}>
            Capacity used
          </Grid>
          <Grid item xs={7}>
            {getLocationCapcityUsedText(selectedLocation)}
          </Grid>
          {selectedLocation.capacityAllocated > 0 && (
            <>
              <Grid item xs={5}>
                Capacity allocated
              </Grid>
              <Grid item xs={5}>
                {selectedLocation.capacityAllocated}%
              </Grid>
            </>
          )}
          {selectedLocation.productId && (
            <>
              <Grid item xs={5}>
                Account
              </Grid>
              <Grid item xs={7}>
                {selectedLocation.accountName}
              </Grid>
              <Grid item xs={5}>
                Product
              </Grid>
              <Grid item xs={7}>
                {selectedLocation.productName}
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box
              display="flex"
              mt={0}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button onClick={handleClose}>Close</Button>
            </Box>
          </Grid>
        </Grid>
      </div>
    );
  };

  const handleInfoClick = (evt, location) => {
    debugger;
    if (!selectedLocation || location.id !== selectedLocation.id) return;
    evt.preventDefault();
    evt.stopPropagation();
    setAnchorEl(evt.currentTarget);
  };

  // if (!selectedAisle?.id || bayList?.length === 0) return <></>;
  const open = Boolean(anchorEl);

  return (
    <>
      <Grid item xs={12} style={{ width: "100%" }}>
        <div
          className={
            isSmall ? classes.mapContainerMobile : classes.mapContainer
          }
        >
          {selectedLocation && (
            <Popover
              id="location-popover"
              open={open}
              onClose={handleClose}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <Box p={1}>{getTotalPopoverContent()}</Box>
            </Popover>
          )}
          {bayList.map((bay, bayIdx) => (
            <div display="flex" key={bayIdx}>
              {bay.map((level, idx) => (
                <div className={classes.bayContainer} key={idx}>
                  {level.map((location) => {
                    return (
                      <Location
                        key={location.id}
                        location={location}
                        classes={classes}
                        handleSelectLocation={handleSelectLocation}
                        getLocationCapcityUsedText={getLocationCapcityUsedText}
                        isSmall={isSmall}
                        handleInfoClick={handleInfoClick}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Grid>
    </>
  );
};

const Location = ({
  isSmall,
  location,
  classes,
  handleInfoClick,
  handleSelectLocation,
  getLocationCapcityUsedText,
}) => {
  const { state } = useInboundContext();
  const {
    currentInventory,
    selectedLocation,
    selectedInventoryItem,
    disableSelection,
    searchInventoryItemsMap,
  } = state;

  const avatarClassName = useMemo(() => {
    debugger;
    if (selectedLocation?.id === location.id) {
      return classes.selectedLocationAvatar;
    }
    if (disableSelection) return classes.locationAvatar;
    let available = isBulkLocationAvailable(
      location,
      currentInventory,
      selectedInventoryItem.quantityOnHand,
      selectedInventoryItem.isDamaged,
    );
    const sameInventory = currentInventory.inventoryItems?.items?.find(
      (itm) => itm.locationId === location.id,
    );
    if (available) {
      if (sameInventory) return classes.locationUsedAvailableAvatar;
      return classes.locationAvailableAvatar;
    }
    if (sameInventory) return classes.locationUsedAvatar;
    return classes.locationAvatar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.id,
    selectedLocation?.id,
    selectedInventoryItem?.quantityOnHand,
    selectedInventoryItem?.isDamaged,
  ]);

  const getBadgeContent = (itm) => {
    if (itm.productId)
      return itm.productName && itm.productName.trim().substring(0, 3) + "..";
    return null;
  };

  return location.productId ? (
    <Badge
      badgeContent={getBadgeContent(location)}
      className={classes.badge}
      color="primary"
      key={location.id}
      onClick={(evt) => handleInfoClick(evt, location)}
    >
      <div
        className={avatarClassName}
        onClick={(evt) => handleSelectLocation(location, evt)}
      >
        <Typography align="center" variant="body2">
          {getFullName(location)}
        </Typography>
        <Typography align="center" variant="body2">
          {getLocationCapcityUsedText(location)}
        </Typography>
        {searchInventoryItemsMap[location.id] && (
          <Box className={classes.redCircle}>
            {searchInventoryItemsMap[location.id].length}
          </Box>
        )}
      </div>
    </Badge>
  ) : location.id === selectedInventoryItem?.location?.id ? (
    <Badge
      badgeContent={<Typography>?</Typography>}
      className={classes.badge}
      color="primary"
      key={location.id}
      onClick={(evt) => handleInfoClick(evt, location)}
    >
      <div
        className={avatarClassName}
        onClick={(evt) => handleSelectLocation(location, evt)}
      >
        <Typography align="center" variant="body2">
          {getFullName(location, isSmall)}
        </Typography>
        <Typography align="center" variant="body2">
          {getLocationCapcityUsedText(location)}
        </Typography>
        {searchInventoryItemsMap[location.id] && (
          <Box className={classes.redCircle}>
            {searchInventoryItemsMap[location.id].length}
          </Box>
        )}
      </div>
    </Badge>
  ) : (
    <div
      key={location.id}
      className={avatarClassName}
      onClick={(evt) => handleSelectLocation(location, evt)}
    >
      <Typography align="center" variant="body2">
        {getFullName(location, isSmall)}
      </Typography>
      <Typography align="center" variant="body2">
        {getLocationCapcityUsedText(location)}
      </Typography>
      {searchInventoryItemsMap[location.id] && (
        <Box className={classes.redCircle}>
          {searchInventoryItemsMap[location.id].length}
        </Box>
      )}
    </div>
  );
};

const getFullName = (location, isSmall, size = 8) => {
  if (!location) return "";
  const name = getLocationViewName(location);
  if (!isSmall) return name;
  if (name?.length <= size) return name;
  return `${name}`?.substring(0, size - 3) + "...";
};
